import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../config";
import { useAuth } from "../../App";
import { useLanguage } from "../../contexts/LanguageContext";
import { PACKS, PLANS } from "../../lib/products";
import { amountOf, applePayAvailable, getGooglePayClient, googlePayRequest, molliePost } from "../../lib/mollieCheckout";
import { MollieCardFields } from "./MollieCardFields";
import { VisaLogo, MastercardLogo, AmexLogo, MethodIcon } from "./PaymentLogos";
import { PaymentSuccess } from "./PaymentSuccess";

// Apple Pay / Google Pay retirés : pas de paiement direct possible sans configuration supplémentaire
const METHODS = [
  { id: "paybybank", fr: ["Pay by bank", "Virement instantané depuis votre banque"], en: ["Pay by bank", "Instant transfer from your bank"], oneoff: true },
];

const productInfo = (sku, en) => {
  const pack = PACKS.find((p) => p.sku === sku);
  if (pack) return { title: `${pack.credits} ${en ? "credits" : "crédits"}`, price: pack.price, credits: pack.credits };
  const plan = PLANS.find((p) => p.sku === sku);
  return { title: `Premium ${plan.name[en ? "en" : "fr"]}`, price: `${plan.price}${plan.period[en ? "en" : "fr"]}`, credits: plan.credits };
};

function MethodRow({ id, title, sub, selected, onSelect, right, children }) {
  return (
    <div className={`border-t border-stone-200 first:border-t-0 ${selected ? "bg-[#f7f3ec]" : "bg-white"}`}>
      <button type="button" onClick={onSelect} className="w-full flex items-center gap-3 px-4 py-3.5 text-left" data-testid={`pay-method-${id}`}>
        <span className={`w-[18px] h-[18px] rounded-full border flex items-center justify-center ${selected ? "border-stone-900" : "border-stone-400"}`}>
          {selected && <span className="w-2.5 h-2.5 rounded-full bg-stone-900" />}
        </span>
        <span className="flex-1">
          <span className="block text-[15px] font-medium text-stone-900">{title}</span>
          {sub && <span className="block text-[11px] text-stone-500">{sub}</span>}
        </span>
        {right}
      </button>
      {selected && children && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}

// Formulaire de paiement web intégré (Mollie) : carte, Apple Pay, Google Pay, Pay by bank
export function MollieCheckoutHost() {
  const auth = useAuth();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const en = language === "en";
  const [req, setReq] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [method, setMethod] = useState("creditcard");
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);
  const tokenFn = useRef(null);
  const onCardReady = useCallback((fn) => { tokenFn.current = fn; }, []);

  const applyAccount = useCallback((account) => {
    if (!account) return;
    auth?.setUser?.((prev) => ({ ...prev, ...account }));
    try {
      const stored = JSON.parse(localStorage.getItem("user") || "null");
      if (stored) localStorage.setItem("user", JSON.stringify({ ...stored, ...account }));
    } catch { /* ignore */ }
  }, [auth]);

  const showSuccess = useCallback((sku, account) => {
    applyAccount(account);
    setSuccess({ sku, isSub: sku.startsWith("premium"), credits: productInfo(sku, en).credits });
  }, [applyAccount, en]);

  useEffect(() => {
    const onOpen = async (e) => {
      setReq(e.detail);
      setMethod("creditcard");
      setError("");
      if (!cfg) {
        try {
          setCfg(await (await fetch(`${API}/payments/mollie/config`)).json());
        } catch {
          setError(en ? "Connection error" : "Erreur de connexion");
        }
      }
    };
    window.addEventListener("mollie-checkout:open", onOpen);
    return () => window.removeEventListener("mollie-checkout:open", onOpen);
  }, [cfg, en]);

  // Retour 3D Secure / banque : vérification serveur puis confirmation
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const order = params.get("mollie_order");
    if (!order) return;
    window.history.replaceState({}, "", window.location.pathname);
    let tries = 0;
    const check = async () => {
      try {
        const d = await molliePost(`${API}/payments/mollie/confirm/${order}`);
        if (d.paid) return showSuccess(d.sku, d.account);
        if ((d.status === "open" || d.status === "pending") && tries++ < 8) return setTimeout(check, 2500);
        toast.error(d.status === "pending"
          ? (en ? "Payment pending: your account will be credited as soon as the bank confirms." : "Paiement en attente : votre compte sera crédité dès confirmation de la banque.")
          : (en ? "Payment not completed." : "Paiement non finalisé."));
      } catch (err) {
        toast.error(err.message);
      }
    };
    check();
  }, [showSuccess, en]);

  const close = (result = false) => {
    req?.resolve(result);
    setReq(null);
    setPaying(false);
  };

  const submit = async (extra) => {
    const data = await molliePost(`${API}/payments/mollie/checkout`, {
      sku: req.sku, redirect_base: window.location.origin, ...extra,
    });
    if (data.paid) {
      const sku = req.sku;
      close(true);
      showSuccess(sku, data.account);
      return true;
    }
    if (data.checkout_url) {
      window.location.href = data.checkout_url;
      return true;
    }
    throw new Error(en ? "Payment not completed." : "Paiement non finalisé.");
  };

  const run = async (fn) => {
    setPaying(true);
    setError("");
    try {
      await fn();
    } catch (e) {
      setError(e.message || (en ? "Payment error" : "Erreur de paiement"));
      setPaying(false);
    }
  };

  const payCard = () => run(async () => {
    if (!tokenFn.current) throw new Error(en ? "Card form is loading…" : "Formulaire carte en chargement…");
    const { token, error: err } = await tokenFn.current();
    if (err) throw new Error(err.message);
    await submit({ method: "creditcard", card_token: token });
  });

  const payBank = () => run(() => submit({ method: "paybybank" }));

  const payApple = () => {
    if (!applePayAvailable()) return setError(en ? "Apple Pay is available on Safari with an Apple device." : "Apple Pay est disponible sur Safari avec un appareil Apple.");
    const session = new window.ApplePaySession(3, {
      countryCode: "FR", currencyCode: "EUR", merchantCapabilities: ["supports3DS"],
      supportedNetworks: ["amex", "maestro", "masterCard", "visa", "vPay", "cartesBancaires"],
      total: { label: "Love Connect +", amount: amountOf(req.sku) },
    });
    session.onvalidatemerchant = async (e) => {
      try {
        session.completeMerchantValidation(await molliePost(`${API}/payments/mollie/applepay-session`,
          { validation_url: e.validationURL, domain: window.location.hostname }));
      } catch (err) {
        session.abort();
        setError(err.message);
      }
    };
    session.onpaymentauthorized = (e) => run(async () => {
      try {
        await submit({ method: "applepay", apple_pay_token: JSON.stringify(e.payment.token) });
        session.completePayment(window.ApplePaySession.STATUS_SUCCESS);
      } catch (err) {
        session.completePayment(window.ApplePaySession.STATUS_FAILURE);
        throw err;
      }
    });
    session.begin();
  };

  const payGoogle = () => run(async () => {
    const client = await getGooglePayClient(cfg.testmode);
    let data;
    try {
      data = await client.loadPaymentData(googlePayRequest(cfg, req.sku));
    } catch (e) {
      if (e?.statusCode === "CANCELED") return setPaying(false);
      throw new Error(en ? "Google Pay is not available on this device." : "Google Pay n'est pas disponible sur cet appareil.");
    }
    await submit({ method: "googlepay", google_pay_token: data.paymentMethodData.tokenizationData.token });
  });

  const info = req ? productInfo(req.sku, en) : null;
  const enabled = cfg?.methods || [];
  const methods = METHODS.filter((m) => enabled.includes(m.id) && !(m.oneoff && req?.isSubscription)
    && (m.id !== "googlepay" || cfg?.google_pay_ready));

  return (
    <>
      <AnimatePresence>
        {req && (
          <motion.div
            className="fixed inset-0 z-[250] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => !paying && close(false)} data-testid="mollie-checkout-overlay"
          >
            <motion.div
              className="lux-panel w-full sm:max-w-[480px] max-h-[94vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl"
              initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
              transition={{ type: "spring", damping: 24 }} onClick={(e) => e.stopPropagation()}
              data-testid="mollie-checkout-modal"
            >
              <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-stone-200 bg-[#fbf8f3]">
                <div>
                  <p className="lux-title text-[22px] leading-none tracking-[0.12em]">LOVE CONNECT +</p>
                  <p className="text-[10px] tracking-[0.25em] uppercase text-stone-500 mt-1">{en ? "Secure payment" : "Paiement sécurisé"}</p>
                </div>
                <button type="button" onClick={() => close(false)} disabled={paying} className="p-2 rounded-full hover:bg-stone-200/70" data-testid="mollie-checkout-close">
                  <X className="w-5 h-5 text-stone-700" />
                </button>
              </div>

              <div className="flex items-center justify-between px-6 py-3 bg-[#f4efe6] text-sm" data-testid="mollie-checkout-summary">
                <span className="text-stone-700">{info.title}</span>
                <span className="font-semibold text-stone-900">{info.price}</span>
              </div>

              <div className="px-6 py-6">
                <h3 className="lux-title text-2xl mb-1">{en ? "Payment" : "Paiement"}</h3>
                <p className="text-[13px] text-stone-500 mb-4">{en ? "All transactions are secure and encrypted." : "Toutes les transactions sont sécurisées et chiffrées."}</p>
                {req.isSubscription && (
                  <p className="text-[12px] text-stone-600 mb-4" data-testid="mollie-recurring-note">
                    {en ? "Automatic renewal, cancel anytime from Settings." : "Renouvellement automatique, résiliable à tout moment depuis les Paramètres."}
                  </p>
                )}

                {!cfg ? (
                  <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-stone-500" /></div>
                ) : (
                  <div className="rounded-xl border border-stone-300 overflow-hidden">
                    <MethodRow id="creditcard" title={en ? "Bank card" : "Carte bancaire"} selected={method === "creditcard"}
                      onSelect={() => { setMethod("creditcard"); setError(""); }}
                      right={<span className="flex gap-1.5"><VisaLogo /><MastercardLogo /><AmexLogo /></span>}>
                      <MollieCardFields cfg={cfg} en={en} onReady={onCardReady} />
                    </MethodRow>
                    {methods.map((m) => {
                      const [title, sub] = m[en ? "en" : "fr"];
                      return (
                        <MethodRow key={m.id} id={m.id} title={title} sub={sub} selected={method === m.id}
                          onSelect={() => { setMethod(m.id); setError(""); }} right={<MethodIcon id={m.id} />} />
                      );
                    })}
                  </div>
                )}

                {error && <p className="mt-4 text-sm text-red-700" data-testid="mollie-checkout-error">{error}</p>}

                {cfg && (
                  <PayButton method={method} en={en} paying={paying} price={info.price}
                    onPay={{ creditcard: payCard, paybybank: payBank, applepay: payApple, googlepay: payGoogle }[method]} />
                )}
                <p className="mt-4 flex items-center justify-center gap-1.5 text-[10px] tracking-[0.18em] uppercase text-stone-400">
                  <Lock className="w-3 h-3" /> {en ? "Secured by Mollie" : "Sécurisé par Mollie"}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {success && (
          <PaymentSuccess en={en} isSubscription={success.isSub} credits={success.credits}
            onDone={() => { setSuccess(null); navigate("/discover"); }} />
        )}
      </AnimatePresence>
    </>
  );
}

function PayButton({ method, en, paying, price, onPay }) {
  const label = {
    applepay: <span className="flex items-center gap-1.5"> {en ? "Pay with" : "Payer avec"} <span className="font-semibold">Apple Pay</span></span>,
    googlepay: <span className="flex items-center gap-1.5">{en ? "Pay with" : "Payer avec"} <span className="font-semibold">Google Pay</span></span>,
    paybybank: <span>{en ? "Continue to my bank" : "Continuer vers ma banque"} · {price}</span>,
    creditcard: <span>{en ? "Pay" : "Payer"} {price}</span>,
  }[method];
  return (
    <button type="button" onClick={onPay} disabled={paying}
      className="mt-6 w-full h-14 rounded-xl bg-[#1c1917] text-[#f5ead6] text-[15px] tracking-wide flex items-center justify-center gap-2 transition-[background-color,transform] duration-200 hover:bg-black active:scale-[0.99] disabled:opacity-70"
      data-testid="mollie-pay-btn">
      {paying ? <Loader2 className="w-5 h-5 animate-spin" /> : label}
    </button>
  );
}
